import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getCategories } from '@/lib/api';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ChevronRight, Folder, FolderOpen } from 'lucide-react';
import { cn } from '@/lib/utils';

export const CategorySidebar = ({ selectedCategory, onSelectCategory, className }) => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState([]);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await getCategories();
      setCategories(response.data);
      // Auto-expand first level
      if (response.data.length > 0) {
        setExpandedItems([response.data[0].node_id]);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderCategory = (category, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isSelected = selectedCategory === category.node_id;
    const isExpanded = expandedItems.includes(category.node_id);

    if (hasChildren) {
      return (
        <AccordionItem value={category.node_id} key={category.node_id} className="border-0">
          <AccordionTrigger 
            className={cn(
              "py-2 px-3 text-sm hover:no-underline hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md",
              isSelected && "bg-orange-50 dark:bg-orange-900/20 text-orange-600",
              level > 0 && "pl-6"
            )}
            onClick={() => {
              onSelectCategory(category.node_id);
            }}
          >
            <div className="flex items-center gap-2">
              {isExpanded ? (
                <FolderOpen className="h-4 w-4 text-orange-500" />
              ) : (
                <Folder className="h-4 w-4 text-slate-400" />
              )}
              <span className="font-medium">{category.node_name}</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-0 pt-1">
            <div className="ml-4 border-l border-slate-200 dark:border-slate-700 pl-2">
              {category.children.map((child) => renderCategory(child, level + 1))}
            </div>
          </AccordionContent>
        </AccordionItem>
      );
    }

    return (
      <Button
        key={category.node_id}
        variant="ghost"
        className={cn(
          "w-full justify-start py-2 px-3 h-auto text-sm font-normal",
          isSelected && "bg-orange-50 dark:bg-orange-900/20 text-orange-600",
          level > 0 && "pl-6"
        )}
        onClick={() => onSelectCategory(category.node_id)}
        data-testid={`category-${category.node_id}`}
      >
        <ChevronRight className="h-3 w-3 mr-2 text-slate-400" />
        {category.node_name}
      </Button>
    );
  };

  if (loading) {
    return (
      <div className={cn("w-64 border-r bg-slate-50/50 dark:bg-slate-900/50 p-4", className)}>
        <div className="animate-pulse space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-8 bg-slate-200 dark:bg-slate-700 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <aside className={cn("w-64 border-r bg-slate-50/50 dark:bg-slate-900/50", className)} data-testid="category-sidebar">
      <div className="p-4 border-b">
        <h2 className="font-manrope font-semibold text-sm uppercase tracking-wider text-slate-500">
          {t('filter.category')}
        </h2>
      </div>
      <ScrollArea className="h-[calc(100vh-8rem)]">
        <div className="p-2">
          <Button
            variant="ghost"
            className={cn(
              "w-full justify-start py-2 px-3 h-auto text-sm font-medium mb-2",
              !selectedCategory && "bg-orange-50 dark:bg-orange-900/20 text-orange-600"
            )}
            onClick={() => onSelectCategory(null)}
            data-testid="category-all"
          >
            {t('filter.allCategories')}
          </Button>
          
          <Accordion 
            type="multiple" 
            value={expandedItems}
            onValueChange={setExpandedItems}
            className="space-y-1"
          >
            {categories.map((category) => renderCategory(category))}
          </Accordion>
        </div>
      </ScrollArea>
    </aside>
  );
};
